import { Directive, HostListener, ElementRef, inject, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appNumericFormat]',
  standalone: true,
})
export class AppNumericFormatDirective implements OnInit {
  private el = inject(ElementRef);
  private ngControl = inject(NgControl, { optional: true });
  private isFormatting = false;

  ngOnInit() {
    // Formatear valor inicial
    if (this.ngControl && this.ngControl.value) {
      const value = this.ngControl.value;
      if (value !== null && value !== undefined && value !== '') {
        const numValue = this.parseToNumber(value);
        if (!isNaN(numValue)) {
          this.isFormatting = true;
          const formatted = this.formatNumber(numValue);
          const input = this.resolveInputElement();
          if (input) {
            input.value = formatted;
          }
          if (this.ngControl.control) {
            this.ngControl.control.setValue(formatted, { emitEvent: false });
          }
          this.isFormatting = false;
        }
      }
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    if (this.isFormatting) return;

    const input = this.resolveInputElement(event.target);
    if (!input) return;

    const value = input.value;

    // Permitir vacío
    if (!value || value === '') {
      if (this.ngControl?.control) {
        this.ngControl.control.setValue('', { emitEvent: true });
      }
      return;
    }

    // Guardar posición del cursor
    const cursorPos = input.selectionStart;

    // Obtener valor numérico puro
    let numericValue = this.extractNumericValue(value);

    // Limitar a 2 decimales
    if (numericValue.includes('.')) {
      const parts = numericValue.split('.');
      if (parts[1].length > 2) {
        numericValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }

    // Formatear el valor
    const numValue = parseFloat(numericValue);
    if (!isNaN(numValue)) {
      this.isFormatting = true;
      const formatted = this.formatNumber(numValue);
      input.value = formatted;

      // Ajustar cursor
      const newCursorPos = this.adjustCursorPosition(value, formatted, cursorPos ?? 0);
      this.setSelectionRangeSafe(input, newCursorPos, newCursorPos);

      if (this.ngControl?.control) {
        this.ngControl.control.setValue(formatted, { emitEvent: true });
      }
      this.isFormatting = false;
    } else if (numericValue === '-' || numericValue === '') {
      input.value = numericValue;
      if (this.ngControl?.control) {
        this.ngControl.control.setValue(numericValue, { emitEvent: true });
      }
    }
  }

  @HostListener('blur')
  onBlur() {
    if (this.isFormatting) return;

    const input = this.resolveInputElement();
    if (!input) return;

    const value = input.value;
    if (value && value !== '' && value !== '-') {
      const numValue = this.parseToNumber(value);
      if (!isNaN(numValue)) {
        // Redondear a 2 decimales
        const roundedValue = Math.round(numValue * 100) / 100;
        this.isFormatting = true;
        const formatted = this.formatNumber(roundedValue);
        input.value = formatted;
        if (this.ngControl?.control) {
          this.ngControl.control.setValue(formatted, { emitEvent: true });
        }
        this.isFormatting = false;
      }
    } else {
      if (this.ngControl?.control) {
        this.ngControl.control.setValue('', { emitEvent: true });
      }
      input.value = '';
    }
  }

  @HostListener('focus')
  onFocus() {
    if (this.isFormatting) return;

    const input = this.resolveInputElement();
    if (!input) return;

    const value = input.value;
    if (value && value !== '' && value !== '-') {
      const numValue = this.parseToNumber(value);
      if (!isNaN(numValue)) {
        this.isFormatting = true;
        // Mostrar sin formato para editar
        input.value = numValue.toString();
        if (this.ngControl?.control) {
          this.ngControl.control.setValue(numValue.toString(), { emitEvent: true });
        }
        this.isFormatting = false;
      }
    }
  }

  private resolveInputElement(
    source?: EventTarget | null,
  ): HTMLInputElement | HTMLTextAreaElement | null {
    if (source instanceof HTMLInputElement || source instanceof HTMLTextAreaElement) {
      return source;
    }

    const native = this.el.nativeElement;
    if (native instanceof HTMLInputElement || native instanceof HTMLTextAreaElement) {
      return native;
    }

    return native.querySelector?.('input, textarea') ?? null;
  }

  private setSelectionRangeSafe(
    input: HTMLInputElement | HTMLTextAreaElement,
    start: number,
    end: number,
  ): void {
    if (typeof input.setSelectionRange === 'function') {
      input.setSelectionRange(start, end);
    }
  }

  private extractNumericValue(formattedValue: string): string {
    if (!formattedValue) return '';

    // Eliminar todos los puntos (separadores de miles)
    let cleanValue = formattedValue.replace(/\./g, '');
    // Reemplazar coma por punto (decimal)
    cleanValue = cleanValue.replace(/,/g, '.');
    // Eliminar cualquier otro caracter no numérico excepto punto y signo menos
    cleanValue = cleanValue.replace(/[^\d.-]/g, '');

    return cleanValue;
  }

  private formatNumber(value: number): string {
    if (isNaN(value)) return '';
    // Formato español: punto para miles, coma para decimales
    const formatted = value.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatted;
  }

  private parseToNumber(formattedValue: string): number {
    if (!formattedValue) return 0;
    const cleanValue = this.extractNumericValue(formattedValue);
    const numValue = parseFloat(cleanValue);
    return isNaN(numValue) ? 0 : numValue;
  }

  private adjustCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    if (!oldPosition) return newValue.length;

    // Calcular diferencia en la longitud
    const lengthDiff = newValue.length - oldValue.length;
    let newPosition = oldPosition + lengthDiff;

    // Asegurar que no se salga del rango
    newPosition = Math.max(0, Math.min(newPosition, newValue.length));

    return newPosition;
  }
}
